/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

// =========================================================================
// 1. 全局变量，用于记录当前选中的变体图片
// =========================================================================
let currentVariantImages = null;

// =========================================================================
// 2. 放大查看器弹窗（Gallery Viewer）DOM 更新函数
// =========================================================================
function updateGalleryViewerDOM(viewer, images) {
  const isSingle = images.length === 1;
  viewer.classList.toggle('gallery-viewer--single-image', isSingle);

  const thumbsContainer = viewer.querySelector('.gallery-viewer__thumbs');
  if (!thumbsContainer) return;

  thumbsContainer.innerHTML = images.map((img) => {
    const thumbUrl = img.base_url.includes('width=')
      ? img.base_url.replace(/width=\d+/, 'width=138')
      : `${img.base_url}&width=138`;

    return `
      <a class="gallery-viewer__thumb" href="#" data-zoom-url="${img.zoom_url}" data-media-id="${img.id}">
        <img class="gallery-viewer__thumb-img" src="${thumbUrl}" alt="${img.alt}">
      </a>
    `;
  }).join('');
}

// =========================================================================
// 3. 全局状态同步逻辑（事件委托 + 滚动观察器 + 弹窗拦截修复）
// =========================================================================
let mainSlideObserver = null;

function initGallerySync() {
  const gallery = document.querySelector('.media-gallery__inner');
  if (!gallery) return;

  // 3.1 绑定下方缩略图点击事件
  if (!gallery.dataset.thumbSyncBound) {
    gallery.addEventListener('click', (e) => {
      const thumb = e.target.closest('carousel-slider.thumbnails .slider__item');
      if (thumb) {
        e.preventDefault();
        const mediaId = thumb.dataset.mediaId;
        if (mediaId) {
          updateActiveState(gallery, mediaId);
          scrollToMainImage(gallery, mediaId);
        }
      }
    });
    gallery.dataset.thumbSyncBound = 'true';
  }

  // 3.2 绑定主图滚动监听 (Intersection Observer)
  if (mainSlideObserver) {
    mainSlideObserver.disconnect();
  }
  const mainSlider = gallery.querySelector('.main-image .slider');
  if (mainSlider) {
    mainSlideObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const mediaId = entry.target.dataset.mediaId;
          if (mediaId) {
            updateActiveState(gallery, mediaId);
            scrollToThumbnail(gallery, mediaId);
          }
        }
      });
    }, { root: mainSlider, threshold: 0.6 });

    const mainItems = mainSlider.querySelectorAll('.slider__item');
    mainItems.forEach(item => mainSlideObserver.observe(item));
  }

  // 3.3 核心修复：拦截主图点击，强力热更新第一此打开的旧弹窗
  if (!gallery.dataset.zoomFixBound) {
    gallery.addEventListener('click', (e) => {
      const zoomTrigger = e.target.closest('.show-gallery');
      if (zoomTrigger) {
        const mediaId = zoomTrigger.closest('.slider__item')?.dataset?.mediaId;
        
        // 主题自带的点击事件会在此刻打开弹窗
        // 我们开启一个高速定时器(20ms)，只要弹窗出现在页面上，立刻替换内容并选中对应图片
        let attempts = 0;
        const checkModal = setInterval(() => {
          const viewer = document.querySelector('gallery-viewer');
          if (viewer && currentVariantImages) {
            clearInterval(checkModal); // 找到了，停止定时器
            
            // 更新弹窗里面的缩略图 DOM
            updateGalleryViewerDOM(viewer, currentVariantImages);
            
            // 找到刚被点击的那张图片对应的缩略图，让代码"替你点一下"
            if (mediaId) {
              const targetThumb = viewer.querySelector(`[data-media-id="${mediaId}"]`);
              if (targetThumb) {
                // 暂时移除 href="#" 防止页面往上跳，点完再加回去
                const href = targetThumb.getAttribute('href');
                targetThumb.removeAttribute('href');
                targetThumb.click();
                if (href) targetThumb.setAttribute('href', href);
              }
            }
          }
          if (++attempts > 30) clearInterval(checkModal); // 超过时间找不到就放弃(防止死循环)
        }, 20);
      }
    });
    gallery.dataset.zoomFixBound = 'true';
  }
}

// 辅助函数：更新高亮状态 (is-active)
function updateActiveState(gallery, mediaId) {
  const allItems = gallery.querySelectorAll('.slider__item');
  allItems.forEach((item) => {
    if (item.dataset.mediaId === mediaId) {
      item.classList.add('is-active');
      item.removeAttribute('tabindex');
    } else {
      item.classList.remove('is-active');
      item.setAttribute('tabindex', '-1');
    }
  });
}
function scrollToMainImage(gallery, mediaId) {
  const mainSlider = gallery.querySelector('.main-image .slider');
  const targetSlide = mainSlider?.querySelector(`.slider__item[data-media-id="${mediaId}"]`);
  if (mainSlider && targetSlide) mainSlider.scrollTo({ left: targetSlide.offsetLeft - mainSlider.offsetLeft, behavior: 'smooth' });
}
function scrollToThumbnail(gallery, mediaId) {
  const thumbSlider = gallery.querySelector('carousel-slider.thumbnails .slider');
  const targetThumb = thumbSlider?.querySelector(`.slider__item[data-media-id="${mediaId}"]`);
  if (thumbSlider && targetThumb) thumbSlider.scrollTo({ left: Math.max(0, targetThumb.offsetLeft - thumbSlider.offsetLeft - (thumbSlider.clientWidth / 2) + (targetThumb.clientWidth / 2)), behavior: 'smooth' });
}

document.addEventListener('DOMContentLoaded', initGallerySync);


// =========================================================================
// 4. 变体切换重绘逻辑 (on:variant:change)
// =========================================================================
document.addEventListener('on:variant:change', (event) => {
  const variant = event.detail.variant;
  if (!variant) return;

  const scriptTag = document.getElementById('variant_metafield_data');
  if (!scriptTag) return;

  let metafieldData = {};
  try {
    metafieldData = JSON.parse(scriptTag.textContent);
  } catch (e) {
    return;
  }

  const images = (metafieldData[variant.id] && metafieldData[variant.id].length > 0)
    ? metafieldData[variant.id]
    : metafieldData['default'];

  if (!images || images.length === 0) return;
  
  // 赋值给全局变量，供弹窗拦截修复使用
  currentVariantImages = images;

  const getSrcset = (baseUrl, widths) => widths.map((w) => `${baseUrl.includes('width=') ? baseUrl.replace(/width=\d+/, `width=${w}`) : `${baseUrl}&width=${w}`} ${w}w`).join(', ');
  const mainWidths = [340, 480, 740, 980, 1200, 1400, 1600, 1920, 2560, 3050];
  const thumbWidths = [65, 130];

  // 4.1 更新主图
  const mainSliderGrid = document.querySelector('.main-image .slider__grid');
  if (mainSliderGrid) {
    mainSliderGrid.innerHTML = images.map((img, index) => {
      const isActive = index === 0;
      const activeClass = isActive ? ' is-active' : '';
      const tabindexAttr = isActive ? '' : ' tabindex="-1"';
      const srcset = getSrcset(img.base_url, mainWidths);
      return `
        <div class="slider__item${activeClass}" role="group" aria-roledescription="Slide" aria-label="${index + 1} of ${images.length}"${tabindexAttr} data-media-id="${img.id}">
          <a class="block show-gallery" href="${img.zoom_url}" aria-label="Load image ${index + 1} in gallery view">
            <div id="FeaturedMedia-${img.id}-wrapper" class="product-media-wrapper" data-media-id="${img.id}" tabindex="-1">
              <div class="product-media product-media--image">
                <div class="img-ar img-ar--contain" style="--aspect-ratio: ${img.aspect_ratio}">
                  <img src="${img.base_url}" alt="${img.alt}" srcset="${srcset}" width="${img.width}" height="${img.height}" loading="${isActive ? 'eager' : 'lazy'}" sizes="(min-width: 2200px) 1100px, (min-width: 768px) 50vw, 100vw" class="theme-img">
                </div>
              </div>
            </div>
          </a>
        </div>
      `;
    }).join('');
  }

  // 4.2 更新缩略图
  const thumbSliderGrid = document.querySelector('carousel-slider.thumbnails .slider__grid');
  if (thumbSliderGrid) {
    thumbSliderGrid.innerHTML = images.map((img, index) => `
      <li class="slider__item${index === 0 ? ' is-active' : ''}" data-media-id="${img.id}">
        <a class="thumbnail thumbnail--media-image" href="${img.zoom_url}">
          <div class="img-ar img-ar--contain" style="--aspect-ratio: ${img.aspect_ratio}">
            <img src="${img.base_url}" alt="${img.alt}" srcset="${getSrcset(img.base_url, thumbWidths)}" width="${img.width}" height="${img.height}" sizes="65px" class="theme-img">
          </div>
        </a>
      </li>
    `).join('');
  }

  // 4.3 如果弹窗此时在 DOM 中存在(针对第2次+的点击)，直接更新它
  const activeViewers = document.querySelectorAll('gallery-viewer');
  activeViewers.forEach(viewer => updateGalleryViewerDOM(viewer, images));

  // 4.4 状态重置
  const gallery = document.querySelector('.media-gallery__inner');
  if (gallery) {
    const mainSlider = gallery.querySelector('.main-image .slider');
    const thumbSlider = gallery.querySelector('carousel-slider.thumbnails .slider');
    if (mainSlider) mainSlider.scrollLeft = 0;
    if (thumbSlider) thumbSlider.scrollLeft = 0;
  }
  
  initGallerySync();
  window.dispatchEvent(new Event('resize'));
});

/**
 * Symmetry 主题 - 动态尺寸 (Dimension) & 变体图片解析
 */
(function () {
  /**
   * 核心渲染函数
   */
  function updateVariantDimension(variantId) {
    if (!variantId) return;

    const container = document.querySelector('.outter_dimension_text');
    const modalContainer = document.querySelector('.size-modal__body');
    const tabContainers = document.querySelectorAll('.pit-variant-specs');
    const dataEl = document.getElementById('variant_metafield_dimension_data');

    // 页面若无目标节点或缺乏 Metafield JSON 数据，直接退出
    if ((!container && !modalContainer && tabContainers.length === 0) || !dataEl) return;

    let dimensionMap = {};
    try {
      dimensionMap = JSON.parse(dataEl.textContent || '{}');
    } catch (e) {
      console.error('dimension JSON parse error:', e);
      return;
    }

    const htmlString = dimensionMap[variantId];

    if (!htmlString) {
      if (container) container.textContent = '';
      if (modalContainer) modalContainer.innerHTML = '';
      tabContainers.forEach((c) => (c.innerHTML = ''));
    } else {
      const temp = document.createElement('div');
      temp.innerHTML = htmlString;

      let lines = [];
      const blockElements = temp.querySelectorAll('p, li');

      if (blockElements.length > 0) {
        blockElements.forEach((el) => {
          const txt = el.textContent.trim();
          if (txt) lines.push(txt);
        });
      } else {
        temp.querySelectorAll('br').forEach((br) => br.replaceWith('\n'));
        lines = (temp.textContent || '').split('\n').map((l) => l.trim()).filter(Boolean);
      }

      // 1. 渲染外层简写尺寸 (30 × 20 × 10)
      if (container) {
        const shortText = lines.slice(0, 3).join(' × ');
        container.textContent = shortText;
      }

      // 2. 渲染 Modal 弹窗内列表
      if (modalContainer) {
        const html = lines
          .map((line) => {
            const match = line.match(/^(.*)\s(\d.*)$/);
            if (!match) {
              return `
                <div class="dimension-row">
                  <span class="dimension-label">${line}</span>
                </div>
              `;
            }
            return `
              <div class="dimension-row">
                <span class="dimension-label">${match[1].trim()}</span>
                <span class="dimension-value">${match[2].trim()}</span>
              </div>
            `;
          })
          .join('');
        modalContainer.innerHTML = html;
      }

      // 3. 渲染 Tab 规格列表
      if (tabContainers.length > 0) {
        const tabHtml = lines
          .map((line) => {
            const match = line.match(/^(.*?)(?::?\s+)(\d.*)$/);
            if (!match) {
              return `
                <li class="pit-spec-item" style="padding: 0.75rem 1rem;">
                  <span class="pit-spec-key" style="width: 100%; font-weight: normal; opacity: 0.8;">${line}</span>
                </li>
              `;
            }
            return `
              <li class="pit-spec-item">
                <span class="pit-spec-key">${match[1].trim()}</span>
                <span class="pit-spec-val">${match[2].trim()}</span>
              </li>
            `;
          })
          .join('');
        tabContainers.forEach((c) => (c.innerHTML = tabHtml));
      }
    }

    // 4. 变体外部图片分发逻辑
    const variantImageContainers = document.querySelectorAll('.pit-variant-image-container');
    variantImageContainers.forEach((wrapper) => {
      const mapScript = wrapper.querySelector('.pit-variant-image-map');
      const target = wrapper.querySelector('.pit-variant-image-target');
      if (!mapScript || !target) return;

      let imageMap = {};
      try {
        imageMap = JSON.parse(mapScript.textContent || '{}');
      } catch (e) {
        console.error('variant image JSON parse error:', e);
        return;
      }

      const currentImgData = imageMap[variantId];
      if (currentImgData && currentImgData.url) {
        target.innerHTML = `<img class="pit-extra-img" src="${currentImgData.url}" data-zoom-url="${currentImgData.zoom_url}" alt="${currentImgData.alt || ''}" loading="lazy" />`;
        wrapper.style.display = 'block';
      } else {
        target.innerHTML = '';
        wrapper.style.display = 'none';
      }
    });
  }

  // 挂载到全局
  window.updateVariantDimension = updateVariantDimension;

  /**
   * 获取当前选中的变体 ID 并执行渲染
   */
  function initDimension() {
    // 兼容 Symmetry 主题各种表单形式（包括无变体商品的隐藏 ID 输入框）
    const variantInput = document.querySelector(
      '.js-product-form [name="id"], form[action*="/cart/add"] [name="id"], [name="id"]'
    );
    const variantId = variantInput?.value || window.ShopifyAnalytics?.meta?.selectedVariantId;

    if (variantId) {
      updateVariantDimension(variantId);
    }
  }

  // 机制 A：页面加载完成后初始化（兼容无变体商品）
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => setTimeout(initDimension, 50));
  } else {
    setTimeout(initDimension, 50);
  }

  // 机制 B：监听 Symmetry 主题原生的变体切换冒泡事件 (on:variant:change)
  document.addEventListener('on:variant:change', (evt) => {
    const variantId = evt.detail?.variant?.id;
    if (variantId) {
      updateVariantDimension(variantId);
    }
  });

  // 机制 C：支持 Shopify 主题编辑器后台实时预览加载 (shopify:section:load)
  document.addEventListener('shopify:section:load', () => setTimeout(initDimension, 50));
})();